import { useMemo } from "react";
import { Stack, IconButton } from "@fluentui/react";
import DOMPurify from "dompurify";

import styles from "./Answer.module.css";

import { ChatAppResponse, getCitationFilePath } from "../../api";
import { parseAnswerToHtml } from "./AnswerParser";
import { AnswerIcon } from "./AnswerIcon";

interface Props {
    answer: ChatAppResponse;
    isSelected?: boolean;
    isStreaming: boolean;
    onCitationClicked: (filePath: string) => void;
    onThoughtProcessClicked: () => void;
    onSupportingContentClicked: () => void;
    onFollowupQuestionClicked?: (question: string) => void;
    showFollowupQuestions?: boolean;
}

export const Answer = ({
    answer,
    isSelected,
    isStreaming,
    onCitationClicked,
    onThoughtProcessClicked,
    onSupportingContentClicked,
    onFollowupQuestionClicked,
    showFollowupQuestions
}: Props) => {
    const followupQuestions = answer.choices[0].context.followup_questions;
    const messageContent = answer.choices[0].message.content;
    const parsedAnswer = useMemo(() => parseAnswerToHtml(messageContent, isStreaming, onCitationClicked), [answer]);

    const sanitizedAnswerHtml = DOMPurify.sanitize(parsedAnswer.answerHtml);

    return (
        <Stack className={`${styles.answerContainer} ${isSelected && styles.selected}`} verticalAlign="space-between">
            <Stack.Item>
                <Stack horizontal horizontalAlign="space-between">
                    <AnswerIcon />
                    <div>
                        {/* <IconButton
                            style={{ color: "black" }}
                            iconProps={{ iconName: "Lightbulb" }}
                            title="Show thought process"
                            ariaLabel="Show thought process"
                            onClick={() => onThoughtProcessClicked()}
                            disabled={!answer.choices[0].context.thoughts?.length}
                        /> */}
                        {/* <IconButton
                            style={{ color: "black" }}
                            iconProps={{ iconName: "ClipboardList" }}
                            title="Show supporting content"
                            ariaLabel="Show supporting content"
                            onClick={() => onSupportingContentClicked()}
                            disabled={!answer.choices[0].context.data_points}
                        /> */}
                    </div>
                </Stack>
            </Stack.Item>

            <Stack.Item grow>
                <div className={styles.answerText} dangerouslySetInnerHTML={{ __html: sanitizedAnswerHtml }}></div>
            </Stack.Item>

            {!!parsedAnswer.citations.length && (
                <Stack.Item>
                    <Stack horizontal wrap tokens={{ childrenGap: 5 }}>
                        <span className={styles.citationLearnMore}>Citations:</span>
                        {parsedAnswer.citations.map((x, i) => {
                            const base = x.split("#page")[0];

                            let transformedCitation = "";
                            if (/^FR_SA_(\d+bis|\d+ter|\d+-\d+|Premier|\d+)\.pdf$/.test(base)) {
                                transformedCitation = `Article ${RegExp.$1} de la loi n° 17-95 relative aux sociétés anonymes`;
                            } else if (/^FR_SARL_(\d+bis|\d+-\d+|Premier|\d+)\.pdf$/.test(base)) {
                                transformedCitation = `Article ${RegExp.$1} de la loi n° 5-96 sur la société en nom collectif, la société en commandite simple, la société en commandite par actions, la société à responsabilité limitée et la société en participation`;
                            } else if (/^FR_CC_(\d+)\.pdf$/.test(base)) {
                                transformedCitation = `Article ${RegExp.$1} du Code de Commerce`;
                            } else if (/^FR_DOC_(\d+[-\d]+)\.pdf$/.test(base)) {
                                transformedCitation = `Article ${RegExp.$1} du Dahir des Obligations et des Contrats`;
                            }
                            // LAW_JURIDUCTION_DATE_NNNN-NNNN Pattern
                            else if (/^([A-Z]+)_(CSU|CAF|CACM|CACC)_(\d{8})_(\d+[-\d]*)\.pdf$/.test(base)) {
                                const [, , courtCode = "", date = "", number = ""] =
                                    base.match(/^([A-Z]+)_(CSU|CAF|CACM|CACC)_(\d{8})_(\d+[-\d]*)\.pdf$/) || [];
                                const courtName =
                                    courtCode === "CSU"
                                        ? "Cour de Cassation"
                                        : courtCode === "CAF"
                                        ? "Cour d'Appel de Fès"
                                        : courtCode === "CACM"
                                        ? "Cour d'Appel de Commerce de Marrakech"
                                        : courtCode === "CACC"
                                        ? "Cour d'Appel de Commerce de Casablanca"
                                        : "Juridiction";
                                const formattedDate = new Date(`${date.slice(0, 4)}-${date.slice(4, 6)}-${date.slice(6)}`).toLocaleDateString("fr-FR", {
                                    year: "numeric",
                                    month: "long",
                                    day: "numeric"
                                });
                                transformedCitation = `Arrêt N° ${number} de la ${courtName} du ${formattedDate}`;
                            } else {
                                transformedCitation = ""; // If no match, keep the original
                            }

                            return (
                                transformedCitation && (
                                    <span key={i} className={styles.citation} title={transformedCitation}>
                                        {`${i + 1}. ${transformedCitation}`}
                                    </span>
                                )
                            );
                        })}
                    </Stack>
                </Stack.Item>
            )}

            {!!followupQuestions?.length && showFollowupQuestions && onFollowupQuestionClicked && (
                <Stack.Item>
                    <Stack horizontal wrap className={`${!!parsedAnswer.citations.length ? styles.followupQuestionsList : ""}`} tokens={{ childrenGap: 6 }}>
                        <span className={styles.followupQuestionLearnMore}>Follow-up questions:</span>
                        {followupQuestions.map((x, i) => {
                            return (
                                <a key={i} className={styles.followupQuestion} title={x} onClick={() => onFollowupQuestionClicked(x)}>
                                    {`${x}`}
                                </a>
                            );
                        })}
                    </Stack>
                </Stack.Item>
            )}
        </Stack>
    );
};
